import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  ChevronLeft, ChevronRight, MapPin, Clock, AlertTriangle,
  ArrowLeftRight, Calendar, Users, TrendingUp, Megaphone, CalendarOff, Briefcase, Send, CalendarPlus, X, Printer, CheckCircle2, PhoneOff, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, addDays, addWeeks, subWeeks, isToday, isBefore } from 'date-fns';
import { formatTime, formatTimeRange } from '../utils/time';
import { computeMergedHours } from '../utils/hours';
import { generateShiftIcs, generateShiftsIcs, downloadIcs } from '../utils/calendar';
import logger from '../utils/logger';
import { ShiftRequestDialog, ApplyDialog, TimeOffDialog, CallOutDialog } from './employeeschedule/Dialogs';

const SERVICE_LABELS = { day_program: 'Explore Program', in_home: 'IHCS', employment: 'Employment', private_pay: 'Private Pay' };
const SERVICE_COLORS = {
  day_program: { bg: 'bg-[#EDE7F6]', text: 'text-[#6B3FA0]', border: 'border-[#9B7ED8]', bar: 'bg-[#9B7ED8]' },
  in_home: { bg: 'bg-[#E8F5E9]', text: 'text-[#1B5E20]', border: 'border-[#81C784]', bar: 'bg-[#81C784]' },
  employment: { bg: 'bg-[#FFF3E0]', text: 'text-[#E65100]', border: 'border-[#FFB74D]', bar: 'bg-[#FFB74D]' },
  private_pay: { bg: 'bg-[#E1F5FE]', text: 'text-[#01579B]', border: 'border-[#4FC3F7]', bar: 'bg-[#4FC3F7]' },
};
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function EmployeeSchedule() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [shifts, setShifts] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [recentShifts, setRecentShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [maxHours, setMaxHours] = useState(40);
  const [requestDialog, setRequestDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [requestType, setRequestType] = useState('cover');
  const [reason, setReason] = useState('');
  const [targetEmployeeId, setTargetEmployeeId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [closures, setClosures] = useState([]);
  const [openPostings, setOpenPostings] = useState([]);
  const [applyDialog, setApplyDialog] = useState(false);
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyMsg, setApplyMsg] = useState('');
  const [applying, setApplying] = useState(false);
  const [myTimeOff, setMyTimeOff] = useState([]);
  const [timeOffDialog, setTimeOffDialog] = useState(false);
  const [toForm, setToForm] = useState({ start_date: '', end_date: '', type: 'pto', reason: '' });
  const [toSaving, setToSaving] = useState(false);
  const [openShifts, setOpenShifts] = useState([]);
  const [payPeriod, setPayPeriod] = useState(null);
  const [coworkers, setCoworkers] = useState([]);
  const [callOutDialog, setCallOutDialog] = useState(null);
  const [callOutReason, setCallOutReason] = useState('');
  const [callOutSubmitting, setCallOutSubmitting] = useState(false);
  const [myClaimRequests, setMyClaimRequests] = useState([]);
  const [myOnCall, setMyOnCall] = useState([]);

  const weekStart = format(currentWeek, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(currentWeek, 6), 'yyyy-MM-dd');
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);
  const upcomingClosures = useMemo(() => closures.filter(c => c.date >= format(new Date(), 'yyyy-MM-dd')).slice(0, 5), [closures]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const todayOc = format(new Date(), 'yyyy-MM-dd');
      const ocStart = weekStart < todayOc ? weekStart : todayOc;
      const ocFuture = format(addDays(new Date(), 45), 'yyyy-MM-dd');
      const ocEnd = weekEndStr > ocFuture ? weekEndStr : ocFuture;
      const [shiftsRes, upcomingRes, annRes, closRes, postRes, toRes, openRes, payRes, ocRes] = await Promise.all([
        api.get(`/shifts?week_start=${weekStart}&employee_id=${user?.id}`),
        api.get(`/shifts?employee_id=${user?.id}`),
        api.get('/announcements'),
        api.get('/closures'),
        api.get('/staffing/postings'),
        api.get('/time-off'),
        api.get('/open-shifts'),
        api.get('/my-pay-period'),
        api.get(`/on-call?start=${ocStart}&end=${ocEnd}`).catch(() => ({ data: [] })),
      ]);
      setShifts(shiftsRes.data);
      setMyOnCall((ocRes.data || []).filter(o => o.employee_id === user?.id));
      setAnnouncements((annRes.data || []).filter(a => a.is_active && (a.target === 'all' || a.target === 'staff')));
      setClosures(closRes.data || []);
      setOpenPostings((postRes.data || []).filter(p => p.is_active));
      setMyTimeOff(toRes.data || []);
      setOpenShifts(openRes.data || []);
      setPayPeriod(payRes.data || null);

      // My pending claim requests (to show "Pending" on open-shift cards)
      try {
        const reqRes = await api.get('/shift-requests');
        setMyClaimRequests((reqRes.data || []).filter(r => r.request_type === 'claim' && r.status === 'pending'));
      } catch { /* non-fatal */ }

      // Coworkers (for named swap), excluding self
      try {
        const emp = await api.get('/employees');
        setCoworkers((emp.data || []).filter(e => e.id !== user?.id && e.role === 'staff' && e.is_active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch { /* non-fatal */ }

      // Get upcoming: today + next 14 days beyond current week
      const today = format(new Date(), 'yyyy-MM-dd');
      const all = upcomingRes.data || [];
      const upcoming = all
        .filter(s => s.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date) || a.time_in.localeCompare(b.time_in));
      setUpcomingShifts(upcoming);
      // Recent past shifts (most recent 5) used as a friendly fallback when
      // the current week and upcoming list are both empty so the page never
      // looks "broken" to staff who only have older shifts.
      const recent = all
        .filter(s => s.date < today)
        .sort((a, b) => b.date.localeCompare(a.date) || b.time_in.localeCompare(a.time_in))
        .slice(0, 5);
      setRecentShifts(recent);

      // Get max hours from staff profile
      try {
        const empRes = await api.get('/auth/me');
        setMaxHours(empRes.data?.max_weekly_hours || 40);
      } catch (err) { logger.error('Failed to fetch profile:', err); }
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api, weekStart, weekEndStr, user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch activities for the days this staff actually works (group_number set
  // = Explore Program shift). Cheap — one GET per unique date covered by
  // their shifts this week. Indexed by `${date}|${group_number}` for fast
  // lookup in the day-detail render.
  const [activitiesByShift, setActivitiesByShift] = useState({});
  useEffect(() => {
    const dates = Array.from(new Set(
      shifts.filter(s => s.group_number).map(s => s.date)
    ));
    if (dates.length === 0) { setActivitiesByShift({}); return; }
    let cancelled = false;
    Promise.all(dates.map(d =>
      api.get(`/activities?date=${d}`).then(r => ({ date: d, rows: r.data || [] })).catch(() => ({ date: d, rows: [] }))
    )).then(results => {
      if (cancelled) return;
      const map = {};
      for (const { rows } of results) {
        for (const r of rows) {
          const key = `${r.date}|${r.group_number}`;
          (map[key] = map[key] || []).push(r);
        }
      }
      for (const k in map) map[k].sort((a, b) => (a.time_start || '').localeCompare(b.time_start || ''));
      setActivitiesByShift(map);
    });
    return () => { cancelled = true; };
  }, [api, shifts]);

  const getActivitiesForShift = (shift) => {
    if (!shift?.group_number) return [];
    return activitiesByShift[`${shift.date}|${shift.group_number}`] || [];
  };

  const weekOnCall = useMemo(() => myOnCall.filter(o => o.date >= weekStart && o.date <= weekEndStr), [myOnCall, weekStart, weekEndStr]);
  const onCallFor = (dateStr) => weekOnCall.find(o => o.date === dateStr);

  const weeklyHours = useMemo(() => computeMergedHours([...shifts, ...weekOnCall]), [shifts, weekOnCall]);

  const utilization = maxHours > 0 ? Math.round((weeklyHours / maxHours) * 100) : 0;
  const barColor = utilization >= 85 ? 'bg-[#D9534F]' : utilization >= 60 ? 'bg-[#6B3FA0]' : 'bg-[#9B7ED8]';

  const serviceCounts = useMemo(() => {
    const counts = {};
    shifts.forEach(s => { counts[s.service_line] = (counts[s.service_line] || 0) + 1; });
    return counts;
  }, [shifts]);

  const getShiftsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return shifts.filter(s => s.date === dateStr).sort((a, b) => a.time_in.localeCompare(b.time_in));
  };

  const openRequest = (shift) => {
    setSelectedShift(shift);
    setRequestType('cover');
    setReason('');
    setTargetEmployeeId('');
    setRequestDialog(true);
  };

  const handleSubmitRequest = async () => {
    setSubmitting(true);
    try {
      const payload = { shift_id: selectedShift.id, request_type: requestType, reason };
      if (requestType === 'swap' && targetEmployeeId) payload.target_employee_id = targetEmployeeId;
      await api.post('/shift-requests', payload);
      setRequestDialog(false);
      toast.success('Request submitted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error submitting request'); }
    setSubmitting(false);
  };

  const handleApply = async () => {
    if (!applyTarget) return;
    setApplying(true);
    try {
      await api.post(`/staffing/postings/${applyTarget.id}/apply`, { message: applyMsg });
      setApplyDialog(false);
      setApplyMsg('');
      toast.success('Application submitted!');
      setOpenPostings(prev => prev.map(p => p.id === applyTarget.id ? { ...p, _applied: true } : p));
    } catch (e) {
      const msg = e.response?.data?.detail || 'Error';
      if (msg.includes('already applied')) toast.info('You have already applied to this posting');
      else toast.error(msg);
    }
    setApplying(false);
  };

  const openTimeOff = () => {
    setToForm({ start_date: '', end_date: '', type: 'pto', reason: '' });
    setTimeOffDialog(true);
  };

  const submitTimeOff = async () => {
    if (!toForm.start_date || !toForm.end_date) { toast.error('Pick both start and end dates'); return; }
    setToSaving(true);
    try {
      await api.post('/time-off', toForm);
      toast.success('Time off request submitted');
      setTimeOffDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    setToSaving(false);
  };

  const cancelTimeOff = async (id) => {
    if (!window.confirm('Cancel this request?')) return;
    try { await api.delete(`/time-off/${id}`); toast.success('Request cancelled'); fetchData(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const claimShift = async (shiftId) => {
    if (!window.confirm('Send a claim request to your supervisor for approval?')) return;
    try {
      await api.post(`/shifts/${shiftId}/claim`);
      toast.success('Request sent — waiting on supervisor approval');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit request'); }
  };

  const openCallOut = (shift) => {
    setCallOutDialog(shift);
    setCallOutReason('');
  };

  const submitCallOut = async () => {
    if (!callOutDialog) return;
    setCallOutSubmitting(true);
    try {
      await api.post(`/shifts/${callOutDialog.id}/call-out`, { reason: callOutReason });
      toast.success('Supervisor notified');
      setCallOutDialog(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setCallOutSubmitting(false);
  };

  const addShiftToCalendar = (shift) => {
    try {
      const ics = generateShiftIcs(shift);
      downloadIcs(`shift-${shift.date}-${shift.time_in}`, ics);
      toast.success('Calendar file downloaded — open it to add to your calendar');
    } catch (e) {
      toast.error('Could not generate calendar file');
    }
  };

  const addWeekToCalendar = () => {
    if (!shifts.length) {
      toast.info('No shifts to export this week');
      return;
    }
    try {
      const ics = generateShiftsIcs(shifts);
      downloadIcs(`my-schedule-${weekStart}`, ics);
      toast.success(`${shifts.length} shift${shifts.length === 1 ? '' : 's'} exported`);
    } catch (e) {
      toast.error('Could not generate calendar file');
    }
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse space-y-6">
        <div className="h-8 w-48 bg-[#DED8EB] rounded" />
        <div className="grid grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-[#DED8EB] rounded-lg" />)}</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8 pb-24 md:pb-8 space-y-6" data-testid="employee-schedule">
      {/* Announcements Banner */}
      {announcements.length > 0 && (
        <div className="space-y-2" data-testid="staff-announcements">
          {announcements.map(a => (
            <div key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border ${
              a.priority === 'urgent' ? 'bg-red-50 border-red-300' :
              a.priority === 'important' ? 'bg-[#FFF3E0] border-[#FFB74D]' :
              'bg-[#EDE7F6] border-[#9B7ED8]'
            }`} data-testid={`staff-announcement-${a.id}`}>
              <Megaphone className={`h-4 w-4 shrink-0 mt-0.5 ${a.priority === 'urgent' ? 'text-red-600' : a.priority === 'important' ? 'text-[#E65100]' : 'text-[#6B3FA0]'}`} />
              <div>
                <p className="text-sm font-semibold text-[#2D1B4E]">{a.title}</p>
                {a.body && <p className="text-xs text-[#5A4D6E] mt-0.5">{a.body}</p>}
                <p className="text-[10px] text-[#8E85A0] mt-0.5">From {a.created_by}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Closures Banner */}
      {closures.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="staff-closures">
          {upcomingClosures.map(c => (
            <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-sm" data-testid={`staff-closure-${c.id}`}>
              <CalendarOff className="h-3.5 w-3.5 text-[#D9534F]" />
              <span className="font-semibold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{c.date}</span>
              {c.reason && <span className="text-[#5A4D6E]">— {c.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Pay Period Summary + Open Shifts row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pay-period-and-open">
        {/* Pay Period */}
        {payPeriod && (
          <Card className="border-[#DED8EB] shadow-sm bg-gradient-to-br from-[#EDE7F6] to-white" data-testid="pay-period-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B3FA0]">Current Pay Period</span>
                <span className="text-[10px] text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  {payPeriod.period_start} → {payPeriod.period_end}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 sm:gap-4">
                <div className="shrink-0">
                  <p className="text-3xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{payPeriod.hours}<span className="text-base font-semibold text-[#8E85A0] ml-0.5">h</span></p>
                  <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider">Hours</p>
                </div>
                <div className="flex-1 w-full grid grid-cols-3 gap-2 pb-1 text-center">
                  <div><p className="text-sm font-bold text-[#2D1B4E]">{payPeriod.shifts}</p><p className="text-[9px] text-[#8E85A0] uppercase">Shifts</p></div>
                  <div><p className="text-sm font-bold text-[#2D1B4E]">{payPeriod.days}</p><p className="text-[9px] text-[#8E85A0] uppercase">Days</p></div>
                  <div>
                    <p className={`text-sm font-bold ${payPeriod.overtime_hours > 0 ? 'text-[#E65100]' : 'text-[#2D1B4E]'}`}>{payPeriod.overtime_hours}h</p>
                    <p className="text-[9px] text-[#8E85A0] uppercase">OT</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Open Shifts */}
        <Card className="border-[#DED8EB] shadow-sm" data-testid="open-shifts-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-[#E65100] flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Open Shifts — Request to Pick Up</span>
              <span className="text-[10px] text-[#8E85A0]">{openShifts.length} available</span>
            </div>
            {openShifts.length === 0 ? (
              <p className="text-sm text-[#8E85A0] text-center py-3">No open shifts right now. Nicely done!</p>
            ) : (
              <div className="space-y-1.5 max-h-[200px] sm:max-h-[120px] overflow-y-auto">
                {openShifts.slice(0, 6).map(s => {
                  const pending = myClaimRequests.some(r => r.shift_id === s.id);
                  return (
                    <div key={s.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5 px-2 py-1.5 rounded-md border border-[#DED8EB] bg-white" data-testid={`open-shift-${s.id}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                        <Badge className={`text-[9px] shrink-0 ${
                          s.service_line === 'day_program' ? 'bg-[#EDE7F6] text-[#6B3FA0]' :
                          s.service_line === 'employment' ? 'bg-[#FFF3E0] text-[#E65100]' :
                          'bg-[#E8F5E9] text-[#1B5E20]'
                        }`}>{SERVICE_LABELS[s.service_line] || s.service_line}</Badge>
                        <span className="text-xs font-medium text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.date} · {formatTimeRange(s.time_in, s.time_out)}</span>
                        {s.location && <span className="text-[10px] text-[#8E85A0] truncate">· {s.location}</span>}
                      </div>
                      {pending ? (
                        <Badge className="text-[10px] bg-[#FFF3E0] text-[#E65100] border border-[#FFB74D] gap-1 shrink-0 self-start sm:self-auto" data-testid={`pending-claim-${s.id}`}>
                          <Clock className="h-3 w-3" /> Pending
                        </Badge>
                      ) : (
                        <Button size="sm" onClick={() => claimShift(s.id)} className="h-7 sm:h-6 text-[10px] bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 shrink-0 self-start sm:self-auto" data-testid={`claim-${s.id}`}>
                          <Send className="h-3 w-3" /> Request
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Job Openings */}
      {openPostings.length > 0 && (
        <div className="space-y-2" data-testid="staff-postings">
          <p className="text-xs font-semibold text-[#6B3FA0] uppercase tracking-wider flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5" /> Open Positions
          </p>
          <div className="flex flex-wrap gap-2">
            {openPostings.map(p => (
              <div key={p.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#DED8EB] shadow-sm hover:border-[#9B7ED8] transition-colors" data-testid={`staff-posting-${p.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#2D1B4E]">{p.title}</p>
                  {p.deadline && <p className="text-[10px] text-[#8E85A0]">Deadline: {p.deadline}</p>}
                </div>
                <Button size="sm" onClick={async () => {
                  try {
                    const { data } = await api.get(`/staffing/postings/${p.id}/full`);
                    setApplyTarget(data);
                  } catch { setApplyTarget(p); }
                  setApplyMsg(''); setApplyDialog(true);
                }}
                  disabled={p._applied}
                  className={`shrink-0 gap-1 h-7 text-xs ${p._applied ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white'}`}
                  data-testid={`apply-${p.id}`}>
                  {p._applied ? 'Applied' : <><Send className="h-3 w-3" /> Apply</>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Time Off Requests */}
      {myTimeOff.length > 0 && (
        <div className="space-y-2" data-testid="staff-time-off">
          <p className="text-xs font-semibold text-[#6B3FA0] uppercase tracking-wider flex items-center gap-1.5">
            <CalendarPlus className="h-3.5 w-3.5" /> My Time Off
          </p>
          <div className="flex flex-wrap gap-2">
            {myTimeOff.slice(0, 6).map(to => {
              const tone = to.status === 'approved' ? 'bg-[#E8F5E9] border-[#81C784] text-[#1B5E20]'
                : to.status === 'denied' ? 'bg-red-50 border-red-200 text-[#D9534F]'
                : 'bg-[#FFF3E0] border-[#FFB74D] text-[#E65100]';
              return (
                <div key={to.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs ${tone}`} data-testid={`my-time-off-${to.id}`}>
                  <span className="font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{to.start_date}{to.end_date !== to.start_date ? ` → ${to.end_date}` : ''}</span>
                  <span className="uppercase font-semibold">{to.status}</span>
                  {to.status === 'pending' && (
                    <button onClick={() => cancelTimeOff(to.id)} className="hover:opacity-70" data-testid={`cancel-time-off-${to.id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Schedule
          </h1>
          <p className="text-sm sm:text-base text-[#5A4D6E] mt-1">
            Week of {format(currentWeek, 'MMM d')} - {format(addDays(currentWeek, 6), 'MMM d, yyyy')}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-wrap">
          <Button variant="outline" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))} className="border-[#DED8EB] h-9 w-9" data-testid="emp-prev-week">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 0 }))} className="border-[#DED8EB] text-[#2D1B4E] h-9" data-testid="emp-today-btn">
            Today
          </Button>
          <Button variant="outline" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))} className="border-[#DED8EB] h-9 w-9" data-testid="emp-next-week">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={openTimeOff} className="ml-1 sm:ml-2 bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1.5 h-9" data-testid="request-time-off-btn">
            <CalendarPlus className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Request Time Off</span><span className="sm:hidden">Time Off</span>
          </Button>
          <Button size="icon" variant="outline" onClick={addWeekToCalendar} className="border-[#DED8EB] text-[#1B5E20] h-9 w-9 sm:hidden" data-testid="sync-week-btn-mobile" aria-label="Sync week to calendar" title="Add this week to my calendar">
            <CalendarCheck className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={addWeekToCalendar} className="border-[#DED8EB] text-[#1B5E20] gap-1.5 h-9 hidden sm:inline-flex" data-testid="sync-week-btn" title="Add this week's shifts to your calendar">
            <CalendarCheck className="h-3.5 w-3.5" /> Sync to Calendar
          </Button>
          <Button size="icon" variant="outline" onClick={() => navigate('/print?person=me')} className="border-[#DED8EB] text-[#2D1B4E] h-9 w-9 sm:hidden" data-testid="print-my-schedule-btn-mobile" aria-label="Print my schedule">
            <Printer className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate('/print?person=me')} className="border-[#DED8EB] text-[#2D1B4E] gap-1.5 h-9 hidden sm:inline-flex" data-testid="print-my-schedule-btn">
            <Printer className="h-3.5 w-3.5" /> Print My Schedule
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="my-schedule-stats">
        {/* Hours card */}
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="my-hours-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-[#EDE7F6] rounded-lg flex items-center justify-center">
                  <Clock className="h-4 w-4 text-[#6B3FA0]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider font-semibold text-[#8E85A0]">Weekly Hours</p>
                  <p className="text-2xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {weeklyHours.toFixed(1)}<span className="text-sm text-[#8E85A0] font-normal">/{maxHours}h</span>
                  </p>
                </div>
              </div>
              <span className={`text-lg font-bold ${utilization >= 85 ? 'text-[#D9534F]' : 'text-[#6B3FA0]'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{utilization}%</span>
            </div>
            <div className="w-full h-2.5 bg-[#F0EDF6] rounded-full overflow-hidden">
              <div className={`h-2.5 ${barColor} rounded-full transition-all duration-500`} style={{ width: `${Math.min(utilization, 100)}%` }} />
            </div>
            <p className="text-[10px] text-[#8E85A0] mt-1.5">{(maxHours - weeklyHours).toFixed(1)}h remaining this week</p>
          </CardContent>
        </Card>

        {/* Shifts count */}
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="my-shifts-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-[#E8F5E9] rounded-lg flex items-center justify-center">
                <Calendar className="h-4 w-4 text-[#2E7D32]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-[#8E85A0]">This Week</p>
                <p className="text-2xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{shifts.length} <span className="text-sm text-[#8E85A0] font-normal">shifts</span></p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(serviceCounts).map(([sl, count]) => {
                const c = SERVICE_COLORS[sl];
                return (
                  <Badge key={sl} className={`text-[10px] ${c?.bg} ${c?.text} ${c?.border} border`}>
                    {SERVICE_LABELS[sl]}: {count}
                  </Badge>
                );
              })}
              {shifts.length === 0 && <span className="text-xs text-[#8E85A0]">No shifts this week</span>}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming */}
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="my-upcoming-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 bg-[#FFF3E0] rounded-lg flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-[#E65100]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-[#8E85A0]">Upcoming</p>
                <p className="text-2xl font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{upcomingShifts.length} <span className="text-sm text-[#8E85A0] font-normal">total</span></p>
              </div>
            </div>
            <p className="text-xs text-[#8E85A0]">
              {upcomingShifts.length > 0 ? `Next: ${format(new Date(upcomingShifts[0].date + 'T12:00:00'), 'EEE, MMM d')} at ${formatTime(upcomingShifts[0].time_in)}` : 'No upcoming shifts'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly calendar strip */}
      <div className="flex gap-2" data-testid="my-day-selector">
        {weekDates.map(date => {
          const dateStr = format(date, 'yyyy-MM-dd');
          const today = isToday(date);
          const dayShifts = getShiftsForDate(date);
          const oc = onCallFor(dateStr);
          return (
            <div key={dateStr} className={`flex-1 p-2 rounded-lg border text-center transition-all ${
              today ? 'border-[#6B3FA0] bg-[#6B3FA0] text-white shadow-sm' :
              oc ? 'border-amber-400 bg-amber-50' :
              dayShifts.length > 0 ? 'border-[#9B7ED8] bg-[#EDE7F6]/50' :
              'border-[#DED8EB] bg-white text-[#5A4D6E]'
            }`} data-testid={`my-day-${dateStr}`}>
              <p className="text-[10px] font-semibold uppercase">{WEEK_DAYS[date.getDay()]}</p>
              <p className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{format(date, 'd')}</p>
              {(dayShifts.length > 0 || oc) && (
                <div className="flex justify-center gap-0.5 mt-0.5">
                  {dayShifts.map(s => (
                    <div key={s.id} className={`w-1.5 h-1.5 rounded-full ${SERVICE_COLORS[s.service_line]?.bar || 'bg-gray-300'}`} />
                  ))}
                  {oc && <div className="w-1.5 h-1.5 rounded-full bg-amber-500" title="On-call" data-testid={`oncall-dot-${dateStr}`} />}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Day-by-day detail */}
      <div className="space-y-2" data-testid="my-week-detail">
        {weekDates.map(date => {
          const dayShifts = getShiftsForDate(date);
          const today = isToday(date);
          const past = isBefore(date, new Date()) && !today;
          const isSameDay = today;
          const oc = onCallFor(format(date, 'yyyy-MM-dd'));
          if (dayShifts.length === 0 && !oc) return null;

          return (
            <Card key={date.toISOString()} className={`border shadow-sm ${today ? 'border-[#6B3FA0] ring-1 ring-[#6B3FA0]/10' : 'border-[#DED8EB]'} bg-white ${past ? 'opacity-60' : ''}`}
              data-testid={`my-day-detail-${format(date, 'yyyy-MM-dd')}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 text-center shrink-0">
                    <p className={`text-[10px] font-semibold uppercase tracking-wider ${today ? 'text-[#6B3FA0]' : 'text-[#8E85A0]'}`}>
                      {WEEK_DAYS[date.getDay()]}
                    </p>
                    <p className={`text-xl font-bold ${today ? 'text-[#6B3FA0]' : 'text-[#2D1B4E]'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {format(date, 'd')}
                    </p>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {oc && (
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid={`my-oncall-${oc.date}`}>
                        <span className="text-sm font-semibold shrink-0 text-amber-900" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {formatTimeRange(oc.time_in, oc.time_out)}
                        </span>
                        <Badge className="text-[10px] shrink-0 bg-amber-100 text-amber-900 border border-amber-300">ON-CALL</Badge>
                        <span className="text-xs text-amber-800">You're the on-call staffer — first pick for call-out cover. Counts toward your weekly hours.</span>
                        {oc.notes && <span className="text-[10px] text-amber-700 italic">· {oc.notes}</span>}
                      </div>
                    )}
                    {dayShifts.map(shift => {
                      const c = SERVICE_COLORS[shift.service_line] || {};
                      const activities = getActivitiesForShift(shift);
                      return (
                        <div key={shift.id}
                          className={`flex flex-col rounded-lg border gap-2 ${c.border} ${c.bg}/50 ${shift.has_conflict ? 'ring-2 ring-[#F4A261]' : ''}`}
                          data-testid={`my-shift-${shift.id}`}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 gap-2">
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-wrap">
                            <span className="text-sm font-semibold shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                              {formatTimeRange(shift.time_in, shift.time_out)}
                            </span>
                            <Badge className={`text-[10px] shrink-0 ${c.bg} ${c.text} ${c.border} border`}>
                              {SERVICE_LABELS[shift.service_line]}
                              {shift.group_number ? ` · Group ${shift.group_number}` : ''}
                            </Badge>
                            {(shift.participants || []).length > 0 && (
                              <span className="text-xs text-[#5A4D6E] truncate flex items-center gap-1">
                                <Users className="h-3 w-3 shrink-0" /> {shift.participants.join(', ')}
                              </span>
                            )}
                            {shift.location && (
                              <span className="text-xs text-[#8E85A0] flex items-center gap-0.5">
                                <MapPin className="h-3 w-3" /> {shift.location}
                              </span>
                            )}
                            {shift.has_conflict && (
                              <span className="flex items-center gap-1 text-xs text-[#F4A261] font-medium shrink-0">
                                <AlertTriangle className="h-3 w-3" /> Buffer
                              </span>
                            )}
                          </div>
                          {!past && (
                            <div className="flex items-center gap-1 shrink-0 flex-wrap">
                              <Button variant="ghost" size="sm" onClick={() => addShiftToCalendar(shift)}
                                className="text-[#5A4D6E] hover:text-[#1B5E20] hover:bg-[#E8F5E9] gap-1" data-testid={`add-to-calendar-${shift.id}`}
                                title="Add to Apple/Google Calendar">
                                <CalendarCheck className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Add to Calendar</span><span className="sm:hidden">Calendar</span>
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openRequest(shift)}
                                className="text-[#5A4D6E] hover:text-[#6B3FA0] gap-1" data-testid={`request-shift-${shift.id}`}>
                                <ArrowLeftRight className="h-3.5 w-3.5" /> Request
                              </Button>
                              {isSameDay && (
                                <Button variant="ghost" size="sm" onClick={() => openCallOut(shift)}
                                  className="text-[#D9534F] hover:bg-red-50 gap-1" data-testid={`callout-${shift.id}`}>
                                  <PhoneOff className="h-3.5 w-3.5" /> Call Out
                                </Button>
                              )}
                            </div>
                          )}
                          </div>
                          {activities.length > 0 && (
                            <div className="border-t border-[#DED8EB] bg-white/60 px-3 py-2" data-testid={`activity-plan-${shift.id}`}>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B3FA0] mb-1 flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> Today's plan for Group {shift.group_number}
                              </p>
                              <ul className="space-y-0.5">
                                {activities.map(a => (
                                  <li key={a.id} className="text-xs text-[#2D1B4E] flex items-baseline gap-2" data-testid={`activity-${a.id}`}>
                                    <span className="font-semibold shrink-0 tabular-nums text-[#5A4D6E]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                                      {(a.time_start || '').slice(0, 5)}–{(a.time_end || '').slice(0, 5)}
                                    </span>
                                    <span className="font-medium">{a.activity_name}</span>
                                    {a.location && (
                                      <span className="text-[#8E85A0] text-[11px] truncate">· {a.location}</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                              {activities.some(a => a.notes) && (
                                <details className="mt-1">
                                  <summary className="text-[10px] text-[#8E85A0] cursor-pointer hover:text-[#6B3FA0]">Show notes</summary>
                                  <div className="text-[11px] text-[#5A4D6E] mt-1 space-y-1">
                                    {activities.filter(a => a.notes).map(a => (
                                      <div key={`note-${a.id}`}><strong>{a.activity_name}:</strong> <span className="whitespace-pre-wrap">{a.notes}</span></div>
                                    ))}
                                  </div>
                                </details>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {shifts.length === 0 && weekOnCall.length === 0 && (
          <div className="text-center py-10 text-[#8E85A0]" data-testid="empty-week-state">
            <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">No shifts scheduled this week</p>
            {upcomingShifts.length === 0 && recentShifts.length === 0 ? (
              <p className="text-xs mt-1">No upcoming or recent shifts on file. Contact your supervisor.</p>
            ) : (
              <p className="text-xs mt-1">
                {upcomingShifts.length > 0 ? `${upcomingShifts.length} upcoming shift${upcomingShifts.length === 1 ? '' : 's'} below.` : ''}
                {recentShifts.length > 0 ? ` Most recent: ${format(new Date(recentShifts[0].date + 'T12:00:00'), 'MMM d')}.` : ''}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Recent past shifts (only when current week is empty) */}
      {shifts.length === 0 && recentShifts.length > 0 && (
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="recent-shifts-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Clock className="h-4 w-4 text-[#9B7ED8]" /> Recent shifts
              <Badge className="bg-[#EDE7F6] text-[#5A4D6E] border-0 text-xs">Last {recentShifts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recentShifts.map(s => (
              <div key={s.id} className="flex items-center justify-between p-2 rounded-md border border-[#DED8EB] bg-[#F7F4EF]/40">
                <div>
                  <p className="text-sm font-semibold text-[#2D1B4E]">{format(new Date(s.date + 'T12:00:00'), 'EEE, MMM d')} · {formatTimeRange(s.time_in, s.time_out)}</p>
                  <p className="text-[11px] text-[#8E85A0]">{(s.service_line || '').replace(/_/g, ' ')}{(s.participants || []).length ? ` · ${(s.participants || []).join(', ')}` : ''}</p>
                </div>
                <Badge className="bg-[#EDE7F6] text-[#5A4D6E] border-0 text-[10px]">past</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Upcoming shifts list */}
      {(upcomingShifts.length > 0 || myOnCall.some(o => o.date >= format(new Date(), 'yyyy-MM-dd'))) && (
        <Card className="border-[#DED8EB] shadow-sm bg-white" data-testid="upcoming-shifts-list">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <TrendingUp className="h-4 w-4 text-[#E65100]" /> Upcoming Shifts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="max-h-48">
              <div className="space-y-1.5">
                {[...upcomingShifts,
                  ...myOnCall.filter(o => o.date >= format(new Date(), 'yyyy-MM-dd')).map(o => ({ ...o, id: `oc-${o.date}`, _oncall: true }))]
                  .sort((a, b) => a.date.localeCompare(b.date) || (a.time_in || '').localeCompare(b.time_in || ''))
                  .slice(0, 12).map(shift => {
                  if (shift._oncall) {
                    const ocDate = new Date(shift.date + 'T12:00:00');
                    return (
                      <div key={shift.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-amber-300 bg-amber-50/60" data-testid={`upcoming-${shift.id}`}>
                        <span className="text-xs font-semibold text-[#2D1B4E] w-20 shrink-0">{format(ocDate, 'EEE, MMM d')}</span>
                        <span className="text-xs font-semibold shrink-0 text-amber-900" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(shift.time_in, shift.time_out)}</span>
                        <Badge className="text-[9px] bg-amber-100 text-amber-900 border border-amber-300">On-Call</Badge>
                      </div>
                    );
                  }
                  const c = SERVICE_COLORS[shift.service_line] || {};
                  const shiftDate = new Date(shift.date + 'T12:00:00');
                  return (
                    <div key={shift.id} className={`flex items-center gap-3 p-2.5 rounded-lg border ${c.border} ${c.bg}/30`} data-testid={`upcoming-${shift.id}`}>
                      <span className="text-xs font-semibold text-[#2D1B4E] w-20 shrink-0">{format(shiftDate, 'EEE, MMM d')}</span>
                      <span className="text-xs font-semibold shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(shift.time_in, shift.time_out)}</span>
                      <Badge className={`text-[9px] ${c.bg} ${c.text} ${c.border} border`}>{SERVICE_LABELS[shift.service_line]}</Badge>
                      {shift.location && <span className="text-[10px] text-[#8E85A0] truncate flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{shift.location}</span>}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}


      <ShiftRequestDialog
        open={requestDialog}
        onOpenChange={setRequestDialog}
        selectedShift={selectedShift}
        requestType={requestType}
        setRequestType={setRequestType}
        reason={reason}
        setReason={setReason}
        targetEmployeeId={targetEmployeeId}
        setTargetEmployeeId={setTargetEmployeeId}
        coworkers={coworkers}
        submitting={submitting}
        onSubmit={handleSubmitRequest}
      />

      <ApplyDialog
        open={applyDialog}
        onOpenChange={setApplyDialog}
        applyTarget={applyTarget}
        applyMsg={applyMsg}
        setApplyMsg={setApplyMsg}
        applying={applying}
        onSubmit={handleApply}
      />

      <TimeOffDialog
        open={timeOffDialog}
        onOpenChange={setTimeOffDialog}
        toForm={toForm}
        setToForm={setToForm}
        toSaving={toSaving}
        onSubmit={submitTimeOff}
      />

      <CallOutDialog
        shift={callOutDialog}
        onOpenChange={setCallOutDialog}
        callOutReason={callOutReason}
        setCallOutReason={setCallOutReason}
        callOutSubmitting={callOutSubmitting}
        onSubmit={submitCallOut}
      />

    </div>
  );
}
